// export default function Navbar() {
//   return (
//     <nav className="bg-white shadow-md sticky top-0 z-50">
//       <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
//         {/* Logo / Brand */}
//         <h1 className="text-2xl font-bold text-gray-800">Makrana Marble</h1>

//         {/* Links */}
//         <div className="space-x-6">
//           <a href="/" className="text-gray-700 hover:text-blue-600">Home</a>
//           <a href="/products" className="text-gray-700 hover:text-blue-600">Products</a>
//           <a href="/about" className="text-gray-700 hover:text-blue-600">About</a>
//           <a href="/contact" className="text-gray-700 hover:text-blue-600">Contact</a>
//         </div>
//       </div>
//     </nav>
//   );
// }




import React, { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useKeyboardNavigation, generateAriaId } from "../hooks/useAccessibility";
import { AccessibleButton } from "./common/AccessibleComponents";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { handleKeyNavigation } = useKeyboardNavigation('horizontal');
  
  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/products", label: "Products" },
    { to: "/categories", label: "Categories" },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const menuId = generateAriaId('mobile-menu');

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuKeyDown = (e) => {
    if (e.key === 'Escape') {
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleNavKeyDown = (e, index) => {
    handleKeyNavigation(e, navLinks, index, (newIndex) => {
      const link = document.querySelector(`[data-nav-index="${newIndex}"]`);
      link?.focus();
    });
  };

  return (
    <nav 
      id="main-navigation"
      className="bg-gray-900 text-white shadow-md sticky top-0 z-50 border-b border-gray-800"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* --- Logo / Brand --- */}
          <div className="flex-shrink-0 flex items-center gap-3">
            <Link 
              to="/" 
              className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-md p-1"
              aria-label="Makrana Marble - Go to homepage"
            >
              <img 
                src="/makranamarble.logo.optimized.png" 
                alt="" 
                className="h-8 md:h-10 w-auto" 
                role="presentation"
              />
              <span className="text-2xl md:text-3xl font-extrabold tracking-wide text-yellow-400 font-serif">
                Makrana Marble
              </span>
            </Link>
          </div>

          {/* --- Desktop Navigation Links --- */}
          <div className="hidden md:flex space-x-2 lg:space-x-4" role="menubar">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                data-nav-index={index}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900
                  ${location.pathname === link.to
                    ? "bg-yellow-400 text-gray-900 shadow"
                    : "hover:bg-gray-800 hover:text-yellow-400"}
                `}
                role="menuitem"
                aria-current={location.pathname === link.to ? 'page' : undefined}
                onKeyDown={(e) => handleNavKeyDown(e, index)}
              >
                {link.label}
                {location.pathname === link.to && (
                  <span className="sr-only"> (current page)</span>
                )}
              </Link>
            ))}
          </div>

          {/* --- Mobile Hamburger --- */}
          <div className="md:hidden flex items-center">
            <AccessibleButton
              ref={menuButtonRef}
              onClick={handleMenuToggle}
              variant="ghost"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-yellow-400 hover:bg-gray-800"
              ariaLabel={`${menuOpen ? 'Close' : 'Open'} navigation menu`}
              ariaExpanded={menuOpen}
              ariaControls={menuId}
            >
              {menuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </AccessibleButton>
          </div>
        </div>
      </div>

      {/* --- Mobile Menu --- */}
      {menuOpen && (
        <div 
          id={menuId}
          ref={mobileMenuRef}
          className="md:hidden bg-gray-900 border-t border-gray-800 px-4 pb-4"
          role="menu"
          aria-labelledby="mobile-menu-button"
          onKeyDown={handleMenuKeyDown}
        >
          <div className="flex flex-col space-y-2 mt-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900
                  ${location.pathname === link.to
                    ? "bg-yellow-400 text-gray-900 shadow"
                    : "hover:bg-gray-800 hover:text-yellow-400"}
                `}
                role="menuitem"
                aria-current={location.pathname === link.to ? 'page' : undefined}
                tabIndex={menuOpen ? 0 : -1}
              >
                {link.label}
                {location.pathname === link.to && (
                  <span className="sr-only"> (current page)</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
